import axios from 'axios';
import { API_URL } from '../config/api';

export interface LoginData {
  identifier: string;
  password: string;
}

export interface RequestRestoreAccountOtpData extends LoginData {}

export interface RestoreDeletedAccountData {
  userId: string;
  otp: string;
}

export interface AuthServiceError extends Error {
  code?: string;
  restoreAvailable?: boolean;
  identifier?: string;
  userId?: string;
  deletedDate?: string;
  restoreUntil?: string;
  accountStatus?: string;
  status?: number;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email?: string;
  username: string;
  password: string;
  role: 'TEACHER' | 'STUDENT' | 'SELLER' | 'SPORTS' | 'RIDES';
  phone: string;
  referralCode?: string;
  studentRegistrationMode?: 'SELF' | 'CHILD';
  childFirstName?: string;
  childLastName?: string;
  relationToStudent?: string;
}

export interface RegisterOtpResponse {
  message: string;
  verificationRequired: boolean;
  identifier: string;
  otpExpiresInMinutes: number;
  otp?: string;
}

export interface SportsAccountSwitchData {
  identifier: string;
  otp: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  phone?: string;
  password?: string;
}

export interface RidesAccountSwitchData extends SportsAccountSwitchData {}

class AuthService {
  private resolveErrorDetails(error: unknown, fallback: string) {
    if (!axios.isAxiosError(error)) {
      return { message: fallback };
    }

    const payload = error.response?.data || {};
    const nestedMessage = payload?.message;
    const detail = nestedMessage && typeof nestedMessage === 'object' ? nestedMessage : payload;
    const message =
      typeof nestedMessage === 'string' && nestedMessage.trim()
        ? nestedMessage
        : typeof detail?.message === 'string' && detail.message.trim()
          ? detail.message
          : typeof payload?.error === 'string' && payload.error.trim()
            ? payload.error
            : fallback;

    return {
      message,
      code: typeof detail?.code === 'string' ? detail.code : undefined,
      restoreAvailable: detail?.restoreAvailable === true,
      identifier: typeof detail?.identifier === 'string' ? detail.identifier : undefined,
      userId: typeof detail?.userId === 'string' ? detail.userId : undefined,
      deletedDate: typeof detail?.deletedDate === 'string' ? detail.deletedDate : undefined,
      restoreUntil: typeof detail?.restoreUntil === 'string' ? detail.restoreUntil : undefined,
      accountStatus: typeof detail?.status === 'string' ? detail.status : undefined,
      status: error.response?.status,
    };
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    return this.resolveErrorDetails(error, fallback).message;
  }

  async login(loginData: LoginData) {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        identifier: String(loginData.identifier || '').trim(),
        password: loginData.password,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true
      });

      if (response.data.accessToken) {
        localStorage.setItem('user', JSON.stringify(response.data));
      }
      return response.data;
    } catch (error: unknown) {
      const details = this.resolveErrorDetails(error, 'Login failed. Please try again.');
      const authError = new Error(details.message) as AuthServiceError;
      authError.code = details.code;
      authError.restoreAvailable = details.restoreAvailable;
      authError.identifier = details.identifier;
      authError.userId = details.userId;
      authError.deletedDate = details.deletedDate;
      authError.restoreUntil = details.restoreUntil;
      authError.accountStatus = details.accountStatus;
      authError.status = details.status;
      throw authError;
    }
  }

  async requestRestoreAccountOtp(data: RequestRestoreAccountOtpData) {
    try {
      const response = await axios.post(`${API_URL}/auth/restore-account/request-otp`, {
        identifier: String(data.identifier || '').trim(),
        password: data.password,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      return response.data;
    } catch (error: unknown) {
      throw new Error(this.resolveErrorMessage(error, 'Unable to send restore OTP right now.'));
    }
  }

  async restoreDeletedAccount(data: RestoreDeletedAccountData) {
    try {
      const response = await axios.post(`${API_URL}/auth/restore-account`, {
        userId: data.userId,
        otp: data.otp,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      if (response.data.accessToken) {
        localStorage.setItem('user', JSON.stringify(response.data));
      }
      return response.data;
    } catch (error: unknown) {
      throw new Error(this.resolveErrorMessage(error, 'Unable to restore this account right now.'));
    }
  }

  logout() {
    localStorage.removeItem('user');
  }

  register(registerData: RegisterData) {
    return axios.post(`${API_URL}/auth/register`, registerData);
  }

  verifyRegistrationOtp(identifier: string, otp: string) {
    return axios.post(`${API_URL}/auth/register/verify-otp`, { identifier, otp }, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  resendRegistrationOtp(identifier: string) {
    return axios.post(`${API_URL}/auth/register/resend-otp`, { identifier }, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  requestSportsAccountSwitchOtp(identifier: string) {
    return axios.post(`${API_URL}/auth/sports-switch/request-otp`, { identifier }, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  verifySportsAccountSwitchOtp(data: SportsAccountSwitchData) {
    return axios.post(`${API_URL}/auth/sports-switch/verify-otp`, data, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  requestRidesAccountSwitchOtp(identifier: string) {
    return axios.post(`${API_URL}/auth/rides-switch/request-otp`, { identifier }, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  verifyRidesAccountSwitchOtp(data: RidesAccountSwitchData) {
    return axios.post(`${API_URL}/auth/rides-switch/verify-otp`, data, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  requestForgotPasswordOtp(identifier: string) {
    return axios.post(`${API_URL}/auth/forgot-password/request-otp`, { identifier }, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  resetPasswordWithOtp(identifier: string, otp: string, newPassword: string) {
    return axios.post(`${API_URL}/auth/forgot-password/reset-with-otp`, { identifier, otp, newPassword }, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getAuthHeader() {
    const user = this.getCurrentUser();
    if (user && user.accessToken) {
      return {
        'Authorization': 'Bearer ' + user.accessToken,
        'Content-Type': 'application/json'
      };
    } else {
      return {};
    }
  }

  async updateTeacherProfile(profileData: any) {
    const response = await axios.patch(
      `${API_URL}/teachers/profile`,
      profileData,
      { headers: this.getAuthHeader() }
    );

    if (response.data) {
      const currentUser = this.getCurrentUser();
      const updatedUser = {
        ...currentUser,
        avatar: profileData?.avatar ?? currentUser?.avatar,
        teacherProfile: response.data,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    }

    throw new Error('Failed to update teacher profile');
  }
}

const authService = new AuthService();
export { authService };
